/**
 * Popover for editing a column's name and default categories
 * Shows when clicking a column label on the board
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X, Trash2 } from 'lucide-react';
import { CategoryMultiSelect } from '../../../taskCategories/components/CategoryMultiSelect';
import type { ColumnDefinition } from '../../../types';

interface ColumnEditPopoverProps {
  columnDef: ColumnDefinition;
  x: number;
  y: number;
  onSave: (oldName: string, newName?: string, defaultCategoryIds?: string[]) => void;
  onDelete?: (name: string) => void;
  onClose: () => void;
}

export function ColumnEditPopover({
  columnDef,
  x,
  y,
  onSave,
  onDelete,
  onClose,
}: ColumnEditPopoverProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(columnDef.name);
  const [categoryIds, setCategoryIds] = useState(columnDef.defaultCategoryIds);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  const handleSave = useCallback(() => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const nameChanged = trimmed !== columnDef.name ? trimmed : undefined;
    const catsChanged = JSON.stringify(categoryIds) !== JSON.stringify(columnDef.defaultCategoryIds) ? categoryIds : undefined;
    if (nameChanged !== undefined || catsChanged !== undefined) {
      onSave(columnDef.name, nameChanged, catsChanged);
    }
    onClose();
  }, [name, categoryIds, columnDef, onSave, onClose]);

  const handleCategoryChange = useCallback((newIds: string[]) => {
    setCategoryIds(newIds);
    // Save categories immediately (no need to click save for category toggles)
    onSave(columnDef.name, undefined, newIds);
  }, [columnDef.name, onSave]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
  };

  const handleDelete = () => {
    onDelete?.(columnDef.name);
    onClose();
  };

  // Position: show below/right of click, clamp to viewport
  const popoverStyle: React.CSSProperties = {
    position: 'fixed',
    left: Math.min(x, window.innerWidth - 300),
    top: Math.min(y + 4, window.innerHeight - 300),
    zIndex: 1000,
    width: 280,
  };

  return (
    <div ref={popoverRef} style={popoverStyle}>
      <div
        style={{
          backgroundColor: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
          overflow: 'hidden',
        }}
      >
        {/* Name input */}
        <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
          <label style={{ fontSize: 10, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('columns.columnNameLabel')}
          </label>
          <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
            <input
              ref={inputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              style={{
                flex: 1,
                padding: '4px 8px',
                fontSize: 13,
                border: '1px solid #e2e8f0',
                borderRadius: 4,
                outline: 'none',
                color: '#1e293b',
                backgroundColor: '#fff',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
            />
            <button
              onClick={handleSave}
              style={{
                padding: '4px 8px',
                backgroundColor: '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
              title={t('common.save')}
            >
              <Check size={14} />
            </button>
          </div>
        </div>

        {/* Default categories */}
        <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
          <label style={{ fontSize: 10, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, display: 'block' }}>
            {t('columns.defaultCategories')}
          </label>
          <CategoryMultiSelect
            selectedCategoryIds={categoryIds}
            onChange={handleCategoryChange}
            allowInlineCreate={true}
            compact={true}
          />
        </div>

        {/* Actions */}
        <div style={{ padding: '6px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {onDelete && !showDeleteConfirm && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              style={{
                padding: '4px 8px',
                fontSize: 11,
                color: '#ef4444',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Trash2 size={12} />
              {t('columns.deleteColumn')}
            </button>
          )}
          {showDeleteConfirm && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: '#ef4444' }}>{t('columns.confirmDeleteShort')}</span>
              <button
                onClick={handleDelete}
                style={{
                  padding: '2px 8px',
                  fontSize: 11,
                  color: '#fff',
                  backgroundColor: '#ef4444',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
              >
                {t('common.yes')}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  padding: '2px 8px',
                  fontSize: 11,
                  color: '#64748b',
                  backgroundColor: '#f1f5f9',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
              >
                {t('common.no')}
              </button>
            </div>
          )}
          {!onDelete && <div />}
          <button
            onClick={onClose}
            style={{
              padding: '4px 8px',
              fontSize: 11,
              color: '#64748b',
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <X size={12} />
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
